import { useCallback } from 'react';

import { Boost } from '@packages/types';
import { getBoostModalType, isBoostExtraCancellation } from '@packages/utils';

import { getSelectedBoostById, openModalSelector, useModalStore, useTripStore } from '@store';

/**
 * Gets the appropriate modal component based on boost
 * @param selectedBoost
 */
function getBoostComponentName(selectedBoost?: Boost) {
  const boostModalType = getBoostModalType(selectedBoost);
  switch (boostModalType) {
    case 'addSpecifiedItems':
      return 'AddSpecifiedItemsModal';

    case 'payPerDayCalendar':
      return 'BoostCalendarModal';

    case null:
    default:
      return '';
  }
}
export function useBoostSelectionModal(boostId?: string) {
  const openModal = useModalStore(openModalSelector);
  const selectedBoost = useTripStore(getSelectedBoostById(boostId));

  const onClose = useCallback(async () => {
    if (!isBoostExtraCancellation(selectedBoost)) {
      return;
    }
  }, [selectedBoost]);

  /**
   * Checks to see if the boost has its own selection modal
   * and will display the required modal.
   */
  return useCallback(
    ({
      onSuccess,
      onModalClose,
      showCloseButton = true,
      isCruisePopUp = false,
    }: {
      onSuccess?: () => void;
      onModalClose?: () => void;
      showCloseButton?: boolean;
      isCruisePopUp?: boolean;
    } = {}) => {
      const bodyComponentName = getBoostComponentName(selectedBoost);

      if (!bodyComponentName || !selectedBoost) {
        return;
      }

      return openModal(`Modal`, {
        body: {
          type: bodyComponentName,
          boostId: selectedBoost.boostId,
          onSuccess: onSuccess,
          isCruisePopUp,
        },
        showCloseButton,
        size: 'md',
        bodyContainerClassName: 'mt-2',
        sendExitEvent: true,
        shouldCloseOnOverlayClick: !isCruisePopUp,
        onClose: async () => {
          await onClose?.();
          onModalClose?.();
        },
      });
    },
    [selectedBoost, openModal, onClose],
  );
}
